import React, { useState, useEffect } from "react";
import useCreateCustomer from "@src/hooks/useCreateCustomer";
import useCustomerLogin from "@src/hooks/useCustomerLogin";
import {InputField} from "@project-stories/ecommerce/Account/AccountFields";
import "./ValidationStyles.scss";

const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
const genders = ["Male", "Female", "Prefer Not to Say"];
const states = [
  "Australian Capital Territory",
  "New South Wales",
  "Northern Territory",
  "Queensland",
  "South Australia",
  "Tasmania",
  "Victoria",
  "Western Australia"
]

const AccountRegisterForm = ({ settings, email, isPopup }) => {
    const { is_au, subscription_consent_label } = settings.globals;
    
    const [dependenciesState, setDependenciesState] = useState<any>({ email });
    const [countryOptions, setCountryOptions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState<any>({});

    const { createCustomer, loading, error, success } = useCreateCustomer();
    const { loginCustomer, loading: loginLoading, error: loginError, success: loginSuccess } = useCustomerLogin();
    const phoneLabel = is_au || dependenciesState?.country === 'Australia' ? 'Mobile' : 'Phone';

    // Handle input change and update global dependency state
    const handleInputChange = (e) => {
        const { name, value, type: inputType, checked } = e.target;

        // For checkboxes, use the checked value, otherwise use the input value
        const newValue = inputType === "checkbox" ? checked : value;

        // Update the global dependency state
        updateDependencyState(name, newValue);
        
        // Clear validation error for this field when user starts typing
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Function to update the global dependencies state
    const updateDependencyState = (name, value) => {
        setDependenciesState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    // Validation function
    const validateForm = () => {
        const errors: any = {};
        
        // Required field validation
        if (!dependenciesState.first_name?.trim()) {
            errors.first_name = 'First name is required';
        }
        
        if (!dependenciesState.last_name?.trim()) {
            errors.last_name = 'Last name is required';
        }
        
        if (!email && !dependenciesState.email?.trim()) {
            errors.email = 'Email is required';
        } else if (!email && dependenciesState.email && !/\S+@\S+\.\S+/.test(dependenciesState.email)) {
            errors.email = 'Please enter a valid email address';
        }
        
        if (!dependenciesState.country?.trim() || dependenciesState.country === '---') {
            errors.country = 'Country is required';
        }
        
        if (!dependenciesState.phone?.trim()) {
            errors.phone = `${phoneLabel} number is required`;
        }
        
        if (!dependenciesState.tags?.trim()) {
            errors.tags = 'Birthday month is required';
        }
        
        if (!dependenciesState.gender?.trim()) {
            errors.gender = 'Gender is required';
        }
        
        if (dependenciesState.country === 'Australia' && !dependenciesState.state?.trim()) {
            errors.state = 'State is required for Australian addresses';
        }
        
        if (!dependenciesState.password?.trim()) {
            errors.password = 'Password is required';
        } else if (dependenciesState.password.length < 6) {
            errors.password = 'Password must be at least 6 characters long';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleClick = async (event) => {
        event.preventDefault();
        
        // Validate form before submission
        if (!validateForm()) {
            // Focus on the first field with an error for better accessibility
            const firstErrorField = Object.keys(validationErrors)[0];
            if (firstErrorField) {
                const errorElement = document.getElementById(firstErrorField);
                if (errorElement) {
                    errorElement.focus();
                    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
            return;
        }
        
        // Since this is newsletter, set optIn fields automatically
        const formData = {
            ...dependenciesState,
            ['optInSMS']: true,
            ['optInEmail']: true,
        };

        setDependenciesState(formData);
        setIsSubmitting(true);

        const createResponse  = await createCustomer(formData);
        // console.log('Create customer response:', createResponse);

        if (createResponse) {
            const email = dependenciesState.email;
            const password = dependenciesState.password;

            // Log the customer in after successful creation
            await loginCustomer(email, password);

        }

        setIsSubmitting(false);
    };

    useEffect(() => {
        const countryOptions = document.querySelectorAll("#countryField option");

        if (countryOptions.length) {
            const parsedOptions = Array.from(countryOptions).map((option: any) => ({
                value: option.value,
                label: option.text,
                provinces: option.getAttribute("data-provinces"),
            }));

            setCountryOptions(parsedOptions);

            if (is_au) updateDependencyState('country', 'Australia');
        }

        return () => {
            setCountryOptions([]);
            setDependenciesState({ email });
            setIsSubmitting(false);
            setValidationErrors({});
        }
    }, []);

    return (
        <form id="registerForm" className="space-y-6 account-page__form">
            <InputField label="First Name" id="first_name" type="text" required handleInputChange={handleInputChange} error={validationErrors.first_name} />
            <InputField label="Last Name" id="last_name" type="text" required  handleInputChange={handleInputChange} error={validationErrors.last_name} />
            {!email && <InputField label="Email" id="email" type="email" required handleInputChange={handleInputChange} error={validationErrors.email} />}

            <div className="flex gap-5">
                <div className={`floating-input w-full ${validationErrors.country ? 'has-error' : ''}`}>
                    <select
                        className={`p-2 ${validationErrors.country ? 'border-red-500' : ''}`}
                        id="country"
                        name="country"
                        required
                        value={dependenciesState?.country || ''}
                        onChange={handleInputChange}
                        aria-invalid={validationErrors.country ? 'true' : 'false'}
                        aria-describedby={validationErrors.country ? 'country-error' : undefined}
                    >
                        <option hidden value=""></option>
                        {countryOptions.map((country, index) => (
                            <option
                                key={`${country.value}_${index}`}
                                value={country.value}
                            >
                                {country.label}
                            </option>
                        ))}
                    </select>
                    <label htmlFor="country">Country</label>
                    {validationErrors.country && (
                        <div 
                            id="country-error"
                            className="mt-1 text-sm text-red-500 error-message"
                            role="alert"
                            aria-live="polite"
                        >
                            {validationErrors.country}
                        </div>
                    )}
                </div>

                <InputField label={`${phoneLabel} Number`} id="phone" type="tel" handleInputChange={handleInputChange} error={validationErrors.phone} required />
            </div>

            <div className={`floating-input w-full ${validationErrors.tags ? 'has-error' : ''}`}>
                <select
                    className={`p-2 ${validationErrors.tags ? 'border-red-500' : ''}`}
                    id="birthday"
                    name="tags"
                    required
                    onChange={handleInputChange}
                    aria-invalid={validationErrors.tags ? 'true' : 'false'}
                    aria-describedby={validationErrors.tags ? 'birthday-error' : undefined}
                >
                    <option hidden value=""></option>
                    {months.map((month) => (
                        <option key={month} value={`birthday:${month}`} >
                            {month}
                        </option>
                    ))}
                </select>
                <label htmlFor="birthday">Your birthday month</label>
                {validationErrors.tags && (
                    <div 
                        id="birthday-error"
                        className="mt-1 text-sm text-red-500 error-message"
                        role="alert"
                        aria-live="polite"
                    >
                        {validationErrors.tags}
                    </div>
                )}
            </div>

            {/* Gender radio buttons */}
            <div className={`flex flex-col gap-3 mb-2 ${validationErrors.gender ? 'radio-group-error' : ''}`}>
                <fieldset>
                    <legend className="font-medium">Gender:</legend>
                    <div className="flex gap-3" role="radiogroup" aria-describedby={validationErrors.gender ? 'gender-error' : undefined}>
                        {genders.map(gender => (
                            <label
                                key={gender}
                                className="relative flex-grow text-center"
                            >
                                <input
                                    type="radio"
                                    name="gender"
                                    value={gender}
                                    onChange={handleInputChange}
                                    className="absolute w-0 h-0 opacity-0 peer"
                                    aria-invalid={validationErrors.gender ? 'true' : 'false'}
                                />

                                <span className="inline-block w-full px-4 py-3 text-sm font-bold text-white transition-colors bg-gray-500 cursor-pointer select-none peer-checked:bg-black peer-checked:text-white">
                                    {gender}
                                </span>
                            </label>
                        ))}
                    </div>
                </fieldset>
                {validationErrors.gender && (
                    <div 
                        id="gender-error"
                        className="mt-1 text-sm text-red-500 error-message"
                        role="alert"
                        aria-live="polite"
                    >
                        {validationErrors.gender}
                    </div>
                )}
            </div>

            {dependenciesState.country === 'Australia' &&
                <div className={`floating-input w-full ${validationErrors.state ? 'has-error' : ''}`}>
                    <select
                        className={`p-2 ${validationErrors.state ? 'border-red-500' : ''}`}
                        id="state"
                        name="state"
                        required
                        onChange={handleInputChange}
                        aria-invalid={validationErrors.state ? 'true' : 'false'}
                        aria-describedby={validationErrors.state ? 'state-error' : undefined}
                    >
                        <option hidden value=""></option>
                        {states.map((state) => (
                            <option key={state} value={state}>
                                {state}
                            </option>
                        ))}
                    </select>
                    <label htmlFor="state">State</label>
                    {validationErrors.state && (
                        <div 
                            id="state-error"
                            className="mt-1 text-sm text-red-500 error-message"
                            role="alert"
                            aria-live="polite"
                        >
                            {validationErrors.state}
                        </div>
                    )}
                </div>
            }

            <InputField label="Password" id="password" type="password" required handleInputChange={handleInputChange} error={validationErrors.password} />

            {success && (
                <div
                    className="success-message text-[#36573B]"
                    role="alert"
                >
                    <p className="b3">
                        You have been successfully registered. Redirecting...
                    </p>
                </div>
            )}

            {error && (
                <div className="text-sm text-red-500 error-message" role="alert">
                    <p>{error}</p>
                </div>
            )}

            <div className={'flex flex-col'}>
                <div onClick={handleClick} className="w-full account-page__submit-btn button button-primary">
                    {isSubmitting || loading || loginLoading ? "Submitting..." : "Create Account"}
                </div>
            </div>

            {isPopup
                ?   <p className="mt-4 text-sm text-center" dangerouslySetInnerHTML={{ __html: subscription_consent_label }}>
                    </p>

                :   <p className="text-center account-page__login-link">
                            Already have an account? <a x-on:click="$event.preventDefault(), handleFormToggle(true), isLogin = true"
                            className="underline cursor-pointer account-page__login-btn">Log In
                        </a>
                    </p>
            }
        </form>
    );
};

export default AccountRegisterForm;
